import React, { useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import image_auto_sedan from '../../assets/images/autos/auto_sedan.jpg'
import image_auto_citicar from '../../assets/images/autos/auto_citicar.jpg'
import image_auto_suv from '../../assets/images/autos/auto_suv.jpg'
import image_auto_furgon from '../../assets/images/autos/auto_furgon.jpg'
import image_auto_pickup_doble from '../../assets/images/autos/auto_pickup_doble.jpg'
import image_auto_pickup_simple from '../../assets/images/autos/auto_pickup_simple.jpg'
import image_auto_camion_abierto from '../../assets/images/autos/auto_camion_abierto.jpg'
import image_auto_camion_cerrado from '../../assets/images/autos/auto_camion_cerrado.jpg'
import image_auto_todoterreno from '../../assets/images/autos/auto_todoterreno.jpg'
// Definimos las opciones de vehículos con sus imágenes
const vehicleOptions = [
    { value: 'sedan', label: 'Sedán', image: image_auto_sedan },
    { value: 'citicar', label: 'Citicar', image: image_auto_citicar },
    { value: 'suv', label: 'Suv', image: image_auto_suv },
    { value: 'furgon', label: 'Furgón', image: image_auto_furgon },
    { value: 'pickup_doble', label: 'Pickup doble', image: image_auto_pickup_doble },
    { value: 'pickup_simple', label: 'Pickup simple', image: image_auto_pickup_simple },
    { value: 'camion_abierto', label: 'Camión abierto', image: image_auto_camion_abierto },
    { value: 'camion_cerrado', label: 'Camión cerrado', image: image_auto_camion_cerrado },
    { value: 'todoterreno', label: 'Todo Terreno', image: image_auto_todoterreno }
];

// Opciones de tipos de daño
const damageTypes = [
    { value: 'rayado', label: 'Rayado' },
    { value: 'abollado', label: 'Abollado' },
    { value: 'piquete', label: 'Piquete' },
    { value: 'falta', label: 'Falta' }
];



const VehicleDamageForm = ({ danos = [], onDanosChange }) => {
    const [selectedVehicle, setSelectedVehicle] = useState({ value: 'sedan', label: 'Sedán', image: image_auto_sedan });
    const [selectedDamage, setSelectedDamage] = useState({ value: 'rayado', label: 'Rayado' });
    const [damagePoints, setDamagePoints] = useState(danos);
    const [imageLoaded, setImageLoaded] = useState(false);

    const canvasRef = useRef(null);
    const imageRef = useRef(new Image());

    // Cargar imagen cuando se selecciona un vehículo
    useEffect(() => {
        if (selectedVehicle) {
            setImageLoaded(false);
            imageRef.current = new Image();
            imageRef.current.onload = () => {
                setImageLoaded(true);
                drawImage();
            };
            imageRef.current.src = selectedVehicle.image;
        }
    }, [selectedVehicle]);

    // Dibujar la imagen en el canvas
    const drawImage = () => {
        const canvas = canvasRef.current;
        if (!canvas || !imageRef.current) return;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Dibujar la imagen centrada en el canvas
        const scale = Math.min(canvas.width / imageRef.current.width, canvas.height / imageRef.current.height);
        const x = (canvas.width - imageRef.current.width * scale) / 2;
        const y = (canvas.height - imageRef.current.height * scale) / 2;

        ctx.drawImage(
            imageRef.current,
            x, y,
            imageRef.current.width * scale,
            imageRef.current.height * scale
        );

        // Redibujar los puntos de daño
        drawDamagePoints(ctx);
    };

    // Dibujar los puntos de daño en el canvas
    const drawDamagePoints = (ctx = null) => {
        if (!ctx) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            ctx = canvas.getContext('2d');
            drawImage(); // Redibujar la imagen primero
        }

        damagePoints.forEach(point => {
            const damageColor = getDamageColor(point.tipo_dano);
            const damageShape = getDamageShape(point.tipo_dano);

            ctx.beginPath();
            ctx.fillStyle = damageColor;
            ctx.strokeStyle = '#000';

            ctx.lineWidth = 2;

            // Dibujar figura según el tipo de daño
            switch (damageShape) {
                case 'square':
                    // Cuadrado de 16x16 píxeles centrado en el punto
                    ctx.rect(point.eje_x - 8, point.eje_y - 8, 16, 16);
                    break;

                case 'triangle':
                    // Triángulo equilátero
                    const size = 10; // Tamaño del triángulo
                    ctx.moveTo(point.eje_x, point.eje_y - size); // Vértice superior
                    ctx.lineTo(point.eje_x - size, point.eje_y + size); // Vértice inferior izquierdo
                    ctx.lineTo(point.eje_x + size, point.eje_y + size); // Vértice inferior derecho
                    ctx.closePath(); // Cerrar el triángulo
                    break;

                case 'star':
                    // Estrella de 5 puntas
                    const starSize = 8; // Tamaño de la estrella
                    const spikes = 5; // Número de puntas
                    const outerRadius = starSize;
                    const innerRadius = starSize * 0.5;

                    ctx.moveTo(
                        point.eje_x + outerRadius * Math.cos(Math.PI / 2),
                        point.eje_y - outerRadius * Math.sin(Math.PI / 2)
                    );

                    for (let i = 0; i < spikes * 2; i++) {
                        const radius = i % 2 === 0 ? outerRadius : innerRadius;
                        const angle = Math.PI / 2 - (i * Math.PI) / spikes;

                        ctx.lineTo(
                            point.eje_x + radius * Math.cos(angle),
                            point.eje_y - radius * Math.sin(angle)
                        );
                    }

                    ctx.closePath();
                    break;
                default:
                    // Círculo (figura por defecto)
                    ctx.arc(point.eje_x, point.eje_y, 8, 0, 2 * Math.PI);
                    break;
            }

            ctx.fill();
            ctx.stroke();
        });
        onDanosChange(canvasRef.current.toDataURL('image/png'))
    };

    // Obtener color según el tipo de daño
    const getDamageColor = (damageType) => {
        const colors = {
            rayado: '#ffeb3b',
            abollado: '#ff9800',
            piquete: '#f44336',
            falta: '#0303fc'
        };
        return colors[damageType] || '#000';
    };

    // Obtener figura según el tipo de daño
    const getDamageShape = (damageType) => {
        const shapes = {
            rayado: 'circle',
            abollado: 'square',
            piquete: 'triangle',
            falta: 'star'
        };
        return shapes[damageType] || 'circle';
    };

    // Función para renderizar la figura SVG según el tipo de daño
    const renderDamageShape = (damageType) => {
        const shape = getDamageShape(damageType);
        const color = getDamageColor(damageType);

        switch (shape) {
            case 'square':
                return <rect x="2" y="2" width="12" height="12" fill={color} stroke="#000" strokeWidth="1" />;

            case 'triangle':
                return <polygon points="8,2 14,14 2,14" fill={color} stroke="#000" strokeWidth="1" />;

            case 'star':
                return <path d="M8 1L10 6L15 6L11 9L13 14L8 11L3 14L5 9L1 6L6 6Z" fill="#0303fc" stroke="#000" strokeWidth="1" />;
            case 'circle':
            default:
                return <circle cx="8" cy="8" r="6" fill={color} stroke="#000" strokeWidth="1" />;
        }
    };

    // Manejar clic en el canvas
    const handleCanvasClick = (event) => {
        if (!selectedVehicle || !selectedDamage) {
            alert('Por favor, selecciona un vehículo y un tipo de daño primero');
            return;
        }

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        // Calcular coordenadas relativas al canvas
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // Crear nuevo punto de daño
        const newDamagePoint = {
            eje_x: x,
            eje_y: y,
            tipo_dano: selectedDamage.value,
            tipo_vehiculo: selectedVehicle.value,
        };

        // Agregar a la lista de daños
        setDamagePoints(prev => [...prev, newDamagePoint]);
        onDanosChange([...damagePoints, newDamagePoint]);

        console.log('Daño registrado:', newDamagePoint);
    };

    // Limpiar todos los daños
    const clearDamagePoints = () => {
        setDamagePoints([]);
        onDanosChange([]);
    };

    // Efecto para redibujar cuando cambian los puntos de daño
    useEffect(() => {
        if (imageLoaded) {
            drawDamagePoints();
        }
    }, [damagePoints, imageLoaded]);

    return (
        <div className="vehicle-damage-form">
            {/* Selector de vehículo */}
            <div className="row">
                <div className="col-6">
                    <div className="form-group">
                        <label>Tipo de Vehículo:</label>
                        <Select
                            options={vehicleOptions}
                            value={selectedVehicle}
                            onChange={setSelectedVehicle}
                            placeholder="Selecciona un vehículo"
                            onFocus={(e) => {
                                e.target.readOnly = true;
                            }}
                        />
                    </div>
                </div>
                <div className="col-6">
                    {/* Selector de tipo de daño */}
                    <div className="form-group">
                        <label>Tipo de Daño:</label>
                        <Select
                            options={damageTypes}
                            value={selectedDamage}
                            onChange={setSelectedDamage}
                            placeholder="Selecciona un tipo de daño"
                            onFocus={(e) => {
                                e.target.readOnly = true;
                            }}
                        />
                    </div>
                </div>
            </div>




            {/* Canvas para la imagen y marcado de daños */}
            <div className="canvas-container">
                <canvas
                    ref={canvasRef}
                    width={600}
                    height={400}
                    onClick={handleCanvasClick}
                    style={{
                        border: '2px solid #ccc',
                        cursor: selectedVehicle && selectedDamage ? 'crosshair' : 'not-allowed',
                        backgroundColor: '#f5f5f5',
                    }}
                />
                {!selectedVehicle && (
                    <div className="canvas-placeholder">
                        Selecciona un vehículo para comenzar
                    </div>
                )}
                <p>
                    <div className="legend-container">
                        {damageTypes.map(type => (
                            <div key={type.value} className="legend-item">
                                <svg
                                    className="damage-shape"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    style={{ marginRight: '8px' }}
                                >
                                    {renderDamageShape(type.value)}
                                </svg>
                                {type.label}
                            </div>
                        ))}
                    </div>
                </p>
                <p>Haz clic en la imagen para marcar los daños.</p>
            </div>

            {/* Información y controles */}
            <div className="row">



                <button
                    onClick={clearDamagePoints}
                    disabled={damagePoints.length === 0}
                    className="clear-button"
                >
                    Limpiar Todos los Daños
                </button>

            </div>
        </div>
    );
};

// Estilos CSS (puedes ponerlos en un archivo separado)
const styles = `
.vehicle-damage-form {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
}

.canvas-container {
  position: relative;
  margin: 20px 0;
  text-align: center;
}

.canvas-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #666;
  font-style: italic;
}

.form-info {
  margin-top: 20px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 5px;
}

.damage-legend {
  display: flex;
  gap: 20px;
  margin: 15px 0;
  flex-wrap: wrap;
}

.legend-container {
    display: flex;
    align-items: center; /* Centra horizontalmente */
    justify-content: center; /* Centra horizontalmente */
    flex-wrap: wrap; /* Permite que salten a siguiente línea si no caben */
    gap: 20px; /* Espacio entre items */
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #000;
}

.clear-button {
  background-color: #f44336;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
  margin: 10px 0;
}

.clear-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.clear-button:hover:not(:disabled) {
  background-color: #d32f2f;
}

.damage-summary {
  margin-top: 15px;
}

.damage-summary ul {
  list-style: none;
  padding: 0;
}

.damage-summary li {
  padding: 5px 0;
  border-bottom: 1px solid #eee;
}
`;

// Agregar estilos al documento
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default VehicleDamageForm;